import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExecution, useExecutionResults, useApproveExecution } from '@/hooks/useDocFlow';
import type { IWorkflow, IApprovedResult } from '@/types/docflow';

interface ApprovalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  executionId: string;
  workflow: IWorkflow;
  onApproved: () => void;
}

const ApprovalDialog: React.FC<ApprovalDialogProps> = ({
  isOpen,
  onClose,
  executionId,
  workflow,
  onApproved,
}) => {
  const { t } = useTranslation('docflow');
  const { data: execution, isLoading: execLoading } = useExecution(executionId);
  const { data: results, isLoading: resultsLoading } = useExecutionResults(executionId);
  const approveExecution = useApproveExecution();

  const isRunning = execution?.status === 'running';
  const isPendingApproval = execution?.status === 'pending_approval';

  // Get output fields from workflow's output_designer node
  const outputFields = useMemo(() => {
    const designerNode = workflow.nodes?.find((n) => n.type === 'output_designer');
    return designerNode?.config?.fields || [];
  }, [workflow]);

  // Editable state: map of resultId -> { fieldKey: value }
  const [editedData, setEditedData] = useState<Record<string, Record<string, string>>>({});

  // Initialize editable data when results load
  useEffect(() => {
    if (!results) return;
    const initial: Record<string, Record<string, string>> = {};
    for (const result of results) {
      if (result.status === 'completed' && result.extracted_data) {
        const fields: Record<string, string> = {};
        for (const field of outputFields) {
          fields[field.key] = String(result.extracted_data[field.key] ?? '');
        }
        initial[result.id] = fields;
      }
    }
    setEditedData(initial);
  }, [results, outputFields]);

  const handleFieldChange = useCallback((resultId: string, fieldKey: string, value: string) => {
    setEditedData((prev) => ({
      ...prev,
      [resultId]: {
        ...prev[resultId],
        [fieldKey]: value,
      },
    }));
  }, []);

  const handleApprove = useCallback(() => {
    const approvedResults: IApprovedResult[] = Object.entries(editedData).map(
      ([resultId, fields]) => ({
        result_id: resultId,
        extracted_data: fields as Record<string, unknown>,
      })
    );

    approveExecution.mutate(
      { id: executionId, results: approvedResults },
      { onSuccess: () => onApproved() }
    );
  }, [executionId, editedData, approveExecution, onApproved]);

  const successResults = results?.filter((r) => r.status === 'completed') || [];
  const hasResults = successResults.length > 0;
  // Show spinner while execution is loading/running OR while results are still loading after extraction
  const showSpinner = execLoading || isRunning || (isPendingApproval && resultsLoading);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('execution.approvalTitle')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('execution.approvalSubtitle')}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {showSpinner && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <svg className="animate-spin w-8 h-8 mb-3 text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-sm">
                {t('execution.extractingProgress', {
                  done: execution?.done_files ?? 0,
                  total: execution?.total_files ?? 0,
                })}
              </span>
            </div>
          )}

          {isPendingApproval && !resultsLoading && !hasResults && (
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              {t('execution.noSuccessResults')}
            </div>
          )}

          {isPendingApproval && !resultsLoading && hasResults && successResults.map((result) => (
            <div
              key={result.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {result.file_name}
              </div>
              <div className="px-4 py-3 space-y-3">
                {outputFields.map((field) => (
                  <div key={field.key} className="flex items-center gap-3">
                    <label className="w-40 flex-shrink-0 text-sm text-gray-600 dark:text-gray-400 text-right">
                      {field.label}:
                    </label>
                    <input
                      type="text"
                      value={editedData[result.id]?.[field.key] ?? ''}
                      onChange={(e) => handleFieldChange(result.id, field.key, e.target.value)}
                      className="flex-1 text-sm px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-transparent dark:text-gray-200 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            {t('execution.cancel')}
          </button>
          <button
            onClick={handleApprove}
            disabled={!isPendingApproval || !hasResults || approveExecution.isLoading}
            className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {approveExecution.isLoading
              ? t('execution.approving')
              : t('execution.approveAndSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApprovalDialog;
